import type { ContestType } from '@betterpool/api-types/contests-service';
import { match } from 'ts-pattern';

import { Routes } from '~/domains/common/constants/routes';
import type { League } from '~/domains/commissioner/interfaces/league.interface';
import type {
  PromotedContestCarousel,
  PromotedContestTab,
} from '~/services/marketing/promoted-contests.schema';

type LobbyFilters = { leagueAlias?: string; contestType?: string };

const resolveFilters = (
  filterType: PromotedContestCarousel['filterType'],
  viewMore: PromotedContestCarousel['viewMore'],
  activeTab: PromotedContestTab
): LobbyFilters & { labelParts: (string | undefined)[] } =>
  match(filterType)
    .with('type', () => ({
      leagueAlias: viewMore?.leagueAlias,
      contestType: activeTab.value,
      labelParts: [viewMore?.leagueLabel, activeTab.title],
    }))
    .with('league', () => ({
      leagueAlias: activeTab.value,
      contestType: viewMore?.contestType,
      labelParts: [activeTab.title, viewMore?.contestTypeLabel],
    }))
    .with(null, () => ({
      leagueAlias: viewMore?.leagueAlias,
      contestType: viewMore?.contestType,
      labelParts: [viewMore?.leagueLabel, viewMore?.contestTypeLabel],
    }))
    .exhaustive();

export const buildViewMoreLink = ({
  filterType,
  viewMore,
  activeTab,
  leagues,
}: {
  filterType: PromotedContestCarousel['filterType'];
  viewMore: PromotedContestCarousel['viewMore'];
  activeTab: PromotedContestTab;
  leagues: readonly Pick<League, 'id' | 'alias'>[];
}): { href: string; labelParts: string[] } => {
  const { leagueAlias, contestType, labelParts } = resolveFilters(filterType, viewMore, activeTab);
  const normalizedAlias = leagueAlias?.toLowerCase();
  const leagueId = leagues.find((league) => league.alias.toLowerCase() === normalizedAlias)?.id;

  return {
    href: Routes.contestLobby({
      ...(leagueId && { league: leagueId }),
      ...(contestType && { contestType: contestType as ContestType }),
    }),
    labelParts: labelParts.filter((part): part is string => Boolean(part)),
  };
};
